import React, { useEffect, useMemo, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import PdfButton from "../../ui/PdfButton.jsx";
import { loadCustomers } from "../../../features/shippingHub/shipByJob";
import {
  EMPTY_HISTORY_FILTERS, addLabelBatch, batchLabelJob, fetchBatchPartLabels, labelConfigFor, loadShipmentBatches,
  loadShippedItems, manifestBatchPath, removeLabelBatch, shippedQty, sortRows, updateLabelBatch,
} from "../../../features/shippingHub/shipHistory";
import { printLabelsAt } from "../../../features/salesOrders/palletActions";
import { labelStation } from "../../../features/print/labelStation";
import LoadsCards from "./LoadsCards.jsx";
import SharePdfBlob from "./SharePdfBlob.jsx";
import "../../mobile.css";
import "./byjob.css";

// History on a phone (2026-10-29): the desktop's Shipped Items (pages/ShippingQueuePage.jsx renderShippedTab) and,
// with shipping_signing.delivery / job_loads on, the loads above (./LoadsCards). Reads and requests are
// features/shippingHub/shipHistory.js. Printing from a phone: a manifest opens in the PDF viewer (PdfButton);
// container / part labels of a shipment go to the shop print queue at ui.labelStation; By Task's part labels are a
// POST the queue can't fetch, so that PDF comes back to share. Layout only.

const SORTS = [
  { id: "", label: "As listed" },
  { id: "shipped_date", label: "Date shipped" },
  { id: "product_id", label: "Product ID" },
  { id: "quantity_shipped", label: "Quantity shipped" },
];

export default function HistoryTab({ apiBase, ui, signing }) {
  const showLoads = !!(signing && (signing.delivery || signing.job_loads));
  const station = labelStation(ui && ui.labelStation);
  const [mode, setMode] = useState("shipment");
  const [filters, setFilters] = useState(EMPTY_HISTORY_FILTERS);
  const [customers, setCustomers] = useState([]);
  const [batches, setBatches] = useState({ loading: false, rows: [], searched: false, message: "" });
  const [items, setItems] = useState({ loading: false, rows: [], searched: false, message: "" });
  const [sortField, setSortField] = useState("");
  const [picked, setPicked] = useState({}); // task_id -> true
  const [labelConfig, setLabelConfig] = useState(null); // open = the part-labels panel
  const [labelPdf, setLabelPdf] = useState(null);
  const [making, setMaking] = useState(false);
  const [printing, setPrinting] = useState(null); // `${group}-${which}`
  const [message, setMessage] = useState(null); // {text, ok}

  useEffect(() => {
    let alive = true;
    loadCustomers(apiBase).then((r) => { if (alive && r.ok) setCustomers(r.customers); });
    return () => { alive = false; };
  }, [apiBase]);

  const rows = useMemo(() => sortRows(items.rows, sortField || null, "asc"), [items.rows, sortField]);
  const pickedCount = Object.values(picked).filter(Boolean).length;
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const search = async () => {
    setMessage(null);
    if (mode === "shipment") {
      setBatches((s) => ({ ...s, loading: true }));
      const r = await loadShipmentBatches(apiBase, filters);
      setBatches({ loading: false, rows: r.ok ? r.rows : batches.rows, searched: true, message: r.ok ? "" : r.message });
    } else {
      setItems((s) => ({ ...s, loading: true }));
      setPicked({});
      const r = await loadShippedItems(apiBase, filters);
      setItems({ loading: false, rows: r.ok ? r.rows : items.rows, searched: true, message: r.ok ? "" : r.message });
    }
  };

  const printLabels = async (batch, which) => {
    const g = batch.shipment_group_id;
    if (printing) return;
    setPrinting(`${g}-${which}`);
    setMessage(null);
    try {
      const r = await printLabelsAt({ station: ui && ui.labelStation, ...batchLabelJob(g, which, batch), trigger: "phone_shipping_history" });
      setMessage({ ok: true, text: r.message });
    } catch (err) {
      setMessage({ ok: false, text: `Print ${which === "parts" ? "part" : "container"} labels failed: ${(err && err.message) || err}` });
    } finally {
      setPrinting(null);
    }
  };

  const openLabels = () => { setLabelPdf(null); setLabelConfig(labelConfigFor(rows, picked)); };
  const closeLabels = () => {
    if (labelPdf) setPicked({});
    setLabelConfig(null);
    setLabelPdf(null);
  };
  const makeLabels = async () => {
    if (making) return;
    setMaking(true);
    const r = await fetchBatchPartLabels(apiBase, labelConfig);
    setMaking(false);
    if (!r.ok) { await ui.alert(r.message); return; }
    setLabelPdf(r.blob);
  };

  const filterFields = (
    <div className="jq-sbj-fields">
      <div>
        <label htmlFor="sh-start">Start date</label>
        <input id="sh-start" type="date" className="form-control" value={filters.startDate || ""} onChange={set("startDate")} />
      </div>
      <div>
        <label htmlFor="sh-end">End date</label>
        <input id="sh-end" type="date" className="form-control" value={filters.endDate || ""} onChange={set("endDate")} />
      </div>
      <div className="jq-sbj-wide">
        <label htmlFor="sh-customer">Customer</label>
        <select id="sh-customer" className="form-select" value={filters.customerId} onChange={set("customerId")}>
          <option value="">All customers</option>
          {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
        </select>
      </div>
      {mode === "shipment" ? (
        <div className="jq-sbj-wide">
          <label htmlFor="sh-so">Sales order #</label>
          <input id="sh-so" type="text" className="form-control" placeholder="Filter by SO#" value={filters.salesOrder} onChange={set("salesOrder")} />
        </div>
      ) : (
        <>
          <div className="jq-sbj-wide">
            <label htmlFor="sh-product">Product ID</label>
            <input id="sh-product" type="text" className="form-control" value={filters.productId} onChange={set("productId")} />
          </div>
          <div>
            <label htmlFor="sh-po">Customer PO</label>
            <input id="sh-po" type="text" className="form-control" value={filters.customerPo} onChange={set("customerPo")} />
          </div>
          <div>
            <label htmlFor="sh-cso">Cust SO</label>
            <input id="sh-cso" type="text" className="form-control" value={filters.customerSo} onChange={set("customerSo")} />
          </div>
        </>
      )}
    </div>
  );

  const loadingNow = mode === "shipment" ? batches.loading : items.loading;
  return (
    <div data-testid="phone-ship-history">
      {showLoads && <LoadsCards apiBase={apiBase} />}

      <div className="jq-block">
        <h3>Shipped items</h3>
        <div className="btn-group jq-sbj-seg" role="group" aria-label="Shipped items by">
          <button type="button" className={`btn ${mode === "shipment" ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={mode === "shipment"}
            onClick={() => { setMode("shipment"); setMessage(null); }}>By Shipment</button>
          <button type="button" className={`btn ${mode === "task" ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={mode === "task"}
            onClick={() => { setMode("task"); setMessage(null); }}>By Task</button>
        </div>
        {filterFields}
        <button type="button" className="btn btn-primary w-100" style={{ minHeight: 48 }} disabled={loadingNow} onClick={search}>
          {loadingNow && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
          {mode === "shipment" ? "Search Shipments" : "Load Shipped Items"}
        </button>
      </div>

      {message && (
        <div className={`jq-note ${message.ok ? "jq-note-info" : "jq-note-danger"} d-flex justify-content-between align-items-start`} role="status">
          <span>{message.text}</span>
          <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setMessage(null)} />
        </div>
      )}

      {mode === "shipment" && (
        <div data-testid="phone-history-batches">
          {batches.message && <div className="jq-note jq-note-danger" role="alert">{batches.message}</div>}
          {!batches.loading && !batches.rows.length && (
            <div className="jq-list-state">{batches.searched ? "No shipment batches found." : "Use the filters above and tap Search."}</div>
          )}
          {batches.rows.length > 0 && <div className="small jq-muted mb-2">Labels print at the {station.printer}.</div>}
          {batches.rows.map((b) => (
            <div key={b.shipment_group_id} className="jq-card" data-testid="phone-history-batch">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <div className="fw-bold">{b.sales_order_number || "No sales order"}</div>
                  <div>{b.customer_name}</div>
                </div>
                <span className="small jq-muted text-end">{b.ship_date || "N/A"}</span>
              </div>
              {(b.customer_po || b.customer_so) && (
                <div className="small jq-muted">{[b.customer_po && `PO ${b.customer_po}`, b.customer_so && `Cust SO ${b.customer_so}`].filter(Boolean).join(" · ")}</div>
              )}
              <div className="small mt-1" style={{ overflowWrap: "anywhere" }}>{b.product_ids}</div>
              <div className="small">Qty {b.total_qty} · Containers {b.pallet_count || "—"}{b.container_types ? ` (${b.container_types})` : ""}</div>
              <div className="mt-2">
                <PdfButton url={`${apiBase}${manifestBatchPath(b.shipment_group_id)}`} filename={`Manifest_${b.shipment_group_id}.pdf`}
                  label="Manifest" className="btn btn-outline-primary w-100" testId="phone-history-manifest" />
              </div>
              {b.has_pallets && (
                <div className="jq-sbj-btns">
                  <button type="button" className="btn btn-outline-info" disabled={!!printing} onClick={() => printLabels(b, "pallets")}>
                    {printing === `${b.shipment_group_id}-pallets` && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
                    Container labels
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled={!!printing} onClick={() => printLabels(b, "parts")}>
                    {printing === `${b.shipment_group_id}-parts` && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
                    Part labels
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {mode === "task" && (
        <div className="jq-has-action-bar" data-testid="phone-history-items">
          {items.message && <div className="jq-note jq-note-danger" role="alert">{items.message}</div>}
          {!items.loading && !rows.length && (
            <div className="jq-list-state">{items.searched ? "No shipped items found." : "Use the filters above and tap Load."}</div>
          )}
          {rows.length > 0 && (
            <div className="d-flex gap-2 mb-2">
              <select className="form-select" style={{ minHeight: 44 }} aria-label="Sort by" value={sortField} onChange={(e) => setSortField(e.target.value)}>
                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button type="button" className="btn btn-outline-secondary" style={{ minHeight: 44, whiteSpace: "nowrap" }}
                onClick={() => {
                  if (pickedCount === rows.length) { setPicked({}); return; }
                  const all = {};
                  rows.forEach((l) => { all[l.task_id] = true; });
                  setPicked(all);
                }}>
                {pickedCount === rows.length ? "Untick all" : "Tick all"}
              </button>
            </div>
          )}
          {rows.map((line) => (
            <div key={line.task_id} className="jq-card" data-testid="phone-history-item">
              <div className="jq-sbj-line-top">
                <label className="jq-sbj-check">
                  <input type="checkbox" aria-label={`Labels for ${line.product_id} (job ${line.master_job_num})`} checked={!!picked[line.task_id]}
                    onChange={(e) => setPicked((p) => ({ ...p, [line.task_id]: e.target.checked }))} />
                </label>
                <div className="jq-sbj-body">
                  <div className="fw-bold">{line.product_id}</div>
                  <div className="small jq-muted">Job {line.master_job_num} · {line.description || "N/A"}</div>
                  <div className="small">Shipped {shippedQty(line)} · {line.shipped_date || "N/A"}</div>
                </div>
                {line.image_path && <img src={line.image_path} alt="" className="jq-sbj-thumb" />}
              </div>
              {line.shipment_group_id ? (
                <PdfButton url={`${apiBase}${manifestBatchPath(line.shipment_group_id)}`} filename={`Manifest_${line.shipment_group_id}.pdf`}
                  label="Reprint Manifest" className="btn btn-outline-secondary w-100 mt-2" testId="phone-history-item-manifest" />
              ) : (
                <div className="small jq-muted mt-2">No batch ID (legacy data): no manifest.</div>
              )}
            </div>
          ))}
          {rows.length > 0 && (
            <div className="jq-action-bar" data-testid="phone-history-bar">
              <div className="jq-action-bar-inner">
                <button type="button" className="btn btn-info btn-lg" disabled={pickedCount === 0} onClick={openLabels}>
                  Part labels for selected ({pickedCount})
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <FullScreenPanel open={!!labelConfig} title="Part labels" onClose={making ? undefined : closeLabels} testId="phone-history-labels"
        footer={labelPdf ? (
          <button type="button" className="btn btn-outline-secondary btn-lg w-100" onClick={closeLabels}>Done</button>
        ) : (
          <div className="jq-two-buttons">
            <button type="button" className="btn btn-outline-secondary btn-lg" disabled={making} onClick={closeLabels}>Cancel</button>
            <button type="button" className="btn btn-primary btn-lg" disabled={making} onClick={makeLabels}>
              {making && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}Make the labels
            </button>
          </div>
        )}>
        {labelPdf ? (
          <>
            <p className="jq-dialog-text">The part labels are ready. Print them (AirPrint) or save them to Files.</p>
            <SharePdfBlob blob={labelPdf} filename="Part_Labels.pdf" label="Part labels" testId="phone-history-labels-pdf" />
          </>
        ) : (
          (labelConfig || []).map((part) => (
            <div key={part.part_id} className="jq-card" data-testid="phone-history-label-part">
              <div className="mb-2"><strong>{part.product_id}</strong>{part.description ? `: ${part.description}` : ""}</div>
              {part.batches.map((b) => (
                <div key={b.id} className="jq-sbj-batch-row">
                  <div>
                    <label className="small fw-semibold" htmlFor={`lq-${b.id}`}>Qty on label</label>
                    <input id={`lq-${b.id}`} type="number" inputMode="numeric" min="1" className="form-control" value={b.qty}
                      onChange={(e) => setLabelConfig((c) => updateLabelBatch(c, part.part_id, b.id, "qty", e.target.value))} />
                  </div>
                  <div>
                    <label className="small fw-semibold" htmlFor={`lc-${b.id}`}>Number of labels</label>
                    <input id={`lc-${b.id}`} type="number" inputMode="numeric" min="1" className="form-control" value={b.count}
                      onChange={(e) => setLabelConfig((c) => updateLabelBatch(c, part.part_id, b.id, "count", e.target.value))} />
                  </div>
                  <button type="button" className="btn btn-outline-danger" aria-label="Remove this batch" disabled={part.batches.length <= 1}
                    onClick={() => setLabelConfig((c) => removeLabelBatch(c, part.part_id, b.id))}>
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-primary w-100" style={{ minHeight: 44 }}
                onClick={() => setLabelConfig((c) => addLabelBatch(c, part.part_id))}>
                + Another quantity
              </button>
            </div>
          ))
        )}
      </FullScreenPanel>
    </div>
  );
}
